import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";

/**
 * Custom hook for managing Custom Fee Requests
 *
 * Provides functions to:
 * - Fetch custom fee requests with filters
 * - Create single or bulk custom fee requests
 * - Approve or reject custom fee requests
 * - Update or delete custom fee requests
 */
const useCustomFees = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [customFees, setCustomFees] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  /**
   * Fetch custom fee requests with optional filters
   */
  const fetchCustomFees = useCallback(
    async (filters = {}) => {
      setLoading(true);
      try {
        const queryString = new URLSearchParams(filters).toString();
        const url = queryString
          ? `${domainSettings.urls.finance.customFees}?${queryString}`
          : domainSettings.urls.finance.customFees;

        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          setCustomFees(response.data || []);
          setTotalCount(response.data?.length || 0);
          return response.data;
        } else {
          Utilities.showError(response, "Failed to fetch custom fees");
          setCustomFees([]);
          return null;
        }
      } catch (error) {
        console.error("Error fetching custom fees:", error);
        message.error("An error occurred while fetching custom fees");
        setCustomFees([]);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  /**
   * Get custom fee request by ID
   */
  const getCustomFeeById = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.finance.customFeeDetail.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          return response.data;
        } else {
          Utilities.showError(response, "Failed to fetch custom fee details");
          return null;
        }
      } catch (error) {
        console.error("Error fetching custom fee:", error);
        message.error("An error occurred while fetching custom fee details");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  /**
   * Create a custom fee request
   */
  const createCustomFee = useCallback(
    async (data) => {
      setLoading(true);
      try {
        const response = await Utilities.fetchWithAuth(
          "POST",
          domainSettings.urls.finance.customFees,
          data,
          navigate,
        );

        if (response.ok) {
          message.success("Custom fee request created successfully");
          return response.data;
        } else {
          const errorMsg =
            response.msg ||
            response.data?.error ||
            "Failed to create custom fee request";
          message.error(errorMsg);
          return null;
        }
      } catch (error) {
        console.error("Error creating custom fee:", error);
        message.error("An error occurred while creating custom fee request");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  /**
   * Bulk create custom fee requests for multiple students
   */
  const bulkCreateCustomFees = useCallback(
    async (data) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.finance.customFeeBulk;
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          data,
          navigate,
        );

        if (response.ok) {
          const count = response.data?.createdCount || 0;
          message.success(
            `Successfully created ${count} custom fee request(s)`,
          );
          return response.data;
        } else {
          const errorMsg =
            response.msg ||
            response.data?.error ||
            "Failed to create bulk custom fees";
          message.error(errorMsg);
          return null;
        }
      } catch (error) {
        console.error("Error creating bulk custom fees:", error);
        message.error("An error occurred while creating bulk custom fees");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  /**
   * Approve a custom fee request
   */
  const approveCustomFee = useCallback(
    async (id, data = {}) => {
      try {
        const url = domainSettings.urls.finance.customFeeApprove.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          data,
          navigate,
        );

        if (response.ok) {
          return true;
        } else {
          const errorMsg =
            response.msg ||
            response.data?.error ||
            "Failed to approve custom fee request";
          console.error("Approve error:", errorMsg);
          return false;
        }
      } catch (error) {
        console.error("Error approving custom fee:", error);
        return false;
      }
    },
    [navigate],
  );

  /**
   * Reject a custom fee request
   */
  const rejectCustomFee = useCallback(
    async (id, data) => {
      try {
        const url = domainSettings.urls.finance.customFeeReject.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          data,
          navigate,
        );

        if (response.ok) {
          return true;
        } else {
          const errorMsg =
            response.msg ||
            response.data?.error ||
            "Failed to reject custom fee request";
          console.error("Reject error:", errorMsg);
          return false;
        }
      } catch (error) {
        console.error("Error rejecting custom fee:", error);
        return false;
      }
    },
    [navigate],
  );

  /**
   * Update a custom fee request
   */
  const updateCustomFee = useCallback(
    async (id, data) => {
      try {
        const url = domainSettings.urls.finance.customFeeDetail.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "PATCH",
          url,
          data,
          navigate,
        );

        if (response.ok) {
          return true;
        } else {
          const errorMsg =
            response.msg ||
            response.data?.error ||
            "Failed to update custom fee request";
          console.error("Update error:", errorMsg);
          return false;
        }
      } catch (error) {
        console.error("Error updating custom fee:", error);
        return false;
      }
    },
    [navigate],
  );

  /**
   * Delete a custom fee request
   */
  const deleteCustomFee = useCallback(
    async (id) => {
      try {
        const url = domainSettings.urls.finance.customFeeDetail.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          return true;
        } else {
          const errorMsg =
            response.msg ||
            response.data?.error ||
            "Failed to delete custom fee request";
          console.error("Delete error:", errorMsg);
          return false;
        }
      } catch (error) {
        console.error("Error deleting custom fee:", error);
        return false;
      }
    },
    [navigate],
  );

  return {
    loading,
    customFees,
    totalCount,
    fetchCustomFees,
    getCustomFeeById,
    createCustomFee,
    bulkCreateCustomFees,
    approveCustomFee,
    rejectCustomFee,
    updateCustomFee,
    deleteCustomFee,
  };
};

export default useCustomFees;
